import React, { useState } from 'react';
import { FaTable, FaCode, FaPrint } from 'react-icons/fa';

const FinancialStatementViewer = ({ report }) => {
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'json'

  if (!report) return null;

  const data = report.reportData || {};
  const isPnl = report.reportType === 'profit_and_loss';
  const isBs = report.reportType === 'balance_sheet';

  // Zoho P&L items list
  const pnlSections = data.profit_and_loss || data.profitandloss || [];
  // Zoho Balance Sheet items list
  const bsSections = data.balance_sheet || data.balancesheet || [];

  const mainSections = isPnl ? pnlSections : isBs ? bsSections : [];

  const formatCurrency = (val) => {
    if (val === undefined || val === null) return "$0.00";
    const num = Number(val);
    if (isNaN(num)) return val;
    return num.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  };

  const renderAccountRow = (acc, level = 0) => {
    const paddingLeft = `${level * 1.25 + 1}rem`;
    return (
      <React.Fragment key={acc.account_id || acc.account_name || Math.random()}>
        <tr className="hover:bg-gray-50/80 dark:hover:bg-dark-700/50 transition-colors border-b border-gray-100 dark:border-dark-750">
          <td className="py-2.5 px-4 text-xs text-gray-800 dark:text-gray-200" style={{ paddingLeft }}>
            <span className={level === 0 ? "font-bold text-gray-900 dark:text-white" : "font-normal"}>
              {acc.account_name || acc.name || acc.label}
            </span>
          </td>
          <td className="py-2.5 px-4 text-xs font-mono font-medium text-right text-gray-900 dark:text-white">
            {formatCurrency(acc.total !== undefined ? acc.total : acc.amount)}
          </td>
        </tr>
        {/* Render sub-accounts recursive */}
        {acc.sub_accounts && acc.sub_accounts.map(sub => renderAccountRow(sub, level + 1))}
        {acc.account_transactions && acc.account_transactions.map(sub => renderAccountRow(sub, level + 1))}
      </React.Fragment>
    );
  };

  return (
    <div className="bg-white dark:bg-dark-850 rounded-2xl border border-gray-200 dark:border-dark-700 overflow-hidden shadow-sm">
      {/* Header Bar */}
      <div className="p-4 bg-gray-50/70 dark:bg-dark-800 border-b border-gray-200 dark:border-dark-700 flex flex-wrap items-center justify-between gap-3">
        <div>
          <span className="text-[10px] font-extrabold uppercase tracking-widest text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-950/40 px-2.5 py-1 rounded-full border border-primary-200 dark:border-primary-800">
            {isPnl ? 'Profit & Loss Statement' : isBs ? 'Balance Sheet' : 'Financial Statement'}
          </span>
          <h3 className="text-base font-bold text-gray-900 dark:text-white mt-1.5">
            {report.title || (isPnl ? 'Profit and Loss' : 'Balance Sheet')}
          </h3>
          {report.period && (
            <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">
              Period: {report.period}
            </p>
          )}
        </div>

        {/* View mode toggle */}
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-gray-200 dark:bg-dark-700 p-1 rounded-xl">
            <button
              type="button"
              onClick={() => setViewMode('table')}
              className={`px-3 py-1 text-xs font-bold rounded-lg transition-all flex items-center gap-1.5 ${
                viewMode === 'table'
                  ? 'bg-white dark:bg-dark-900 text-primary-600 dark:text-primary-400 shadow-xs'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900'
              }`}
            >
              <FaTable className="w-3 h-3" /> Table View
            </button>
            <button
              type="button"
              onClick={() => setViewMode('json')}
              className={`px-3 py-1 text-xs font-bold rounded-lg transition-all flex items-center gap-1.5 ${
                viewMode === 'json'
                  ? 'bg-white dark:bg-dark-900 text-primary-600 dark:text-primary-400 shadow-xs'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900'
              }`}
            >
              <FaCode className="w-3 h-3" /> Raw JSON
            </button>
          </div>

          <button
            type="button"
            onClick={() => window.print()}
            className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-700 rounded-xl transition-all"
            title="Print Statement"
          >
            <FaPrint className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Content Section */}
      {viewMode === 'table' ? (
        <div className="overflow-x-auto">
          {mainSections.length === 0 ? (
            <div className="p-8 text-center text-gray-400 text-xs font-medium">
              No detailed account breakdown available for this statement.
            </div>
          ) : (
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-100/60 dark:bg-dark-800 text-gray-500 dark:text-gray-400 uppercase tracking-wider text-[10px] font-extrabold border-b border-gray-200 dark:border-dark-700">
                  <th className="py-3 px-4">Account</th>
                  <th className="py-3 px-4 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {mainSections.map((sec, idx) => (
                  <React.Fragment key={idx}>
                    {/* Section Top Header */}
                    {sec.account_name || sec.total_label || sec.name ? (
                      <tr className="bg-gray-50 dark:bg-dark-800/80 font-bold border-b border-gray-200 dark:border-dark-700">
                        <td className="py-3 px-4 text-xs text-gray-900 dark:text-white uppercase tracking-wider">
                          {sec.account_name || sec.total_label || sec.name}
                        </td>
                        <td className="py-3 px-4 text-xs font-mono font-bold text-right text-gray-900 dark:text-white">
                          {formatCurrency(sec.total)}
                        </td>
                      </tr>
                    ) : null}

                    {/* Account Transactions / Sub items */}
                    {sec.account_transactions && sec.account_transactions.map(item => renderAccountRow(item, 1))}
                    {sec.sub_accounts && sec.sub_accounts.map(item => renderAccountRow(item, 1))}

                    {/* Total Summary Row if label exists */}
                    {sec.total_label && sec.total !== undefined && (
                      <tr className="bg-primary-50/40 dark:bg-primary-950/20 font-extrabold border-t-2 border-b-2 border-primary-200 dark:border-primary-800">
                        <td className="py-3 px-4 text-xs text-primary-900 dark:text-primary-300 uppercase tracking-wider">
                          {sec.total_label}
                        </td>
                        <td className="py-3 px-4 text-xs font-mono font-extrabold text-right text-primary-700 dark:text-primary-400">
                          {formatCurrency(sec.total)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        <div className="p-4 bg-gray-900 text-emerald-400 font-mono text-xs overflow-x-auto max-h-96 custom-scrollbar">
          <pre>{JSON.stringify(data, null, 2)}</pre>
        </div>
      )}
    </div>
  );
};

export default FinancialStatementViewer;
